// src/components/PlatformBlock.js
import React from 'react';
import {
  PLAT_DELIVERABLES, LP_DELIVERABLES, LP_PLATFORMS,
  GARAGE_DOOR_DELIVERABLES, resolveLabel, platShort, platColor,
} from '../constants';

function Tag({ name }) {
  const c = platColor(name);
  return (
    <span style={{
      fontSize: 10, padding: '1px 6px', borderRadius: 3,
      fontWeight: 700, background: c.bg, color: c.text,
    }}>
      {platShort(name)}
    </span>
  );
}

function TogBtn({ active, activeClass, onClick, children }) {
  const styles = {
    built: { bg: '#E6F1FB', color: '#185FA5', border: '#185FA5' },
    posted: { bg: '#EAF3DE', color: '#27500A', border: '#27500A' },
    launched: { bg: '#EAF3DE', color: '#27500A', border: '#27500A' },
    live: { bg: '#dcf0e8', color: '#0F6E56', border: '#0F6E56' },
    audit: { bg: '#EAF3DE', color: '#27500A', border: '#27500A' },
  };
  const s = active ? styles[activeClass] : null;
  return (
    <button onClick={onClick} style={{
      fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
      fontWeight: 600, fontFamily: 'inherit',
      background: active ? s.bg : '#f5f5f3',
      color: active ? s.color : '#6b6b65',
      border: `1px solid ${active ? s.border : 'rgba(0,0,0,0.18)'}`,
    }}>
      {children}
    </button>
  );
}

export default function PlatformBlock({ plat, index, onChange, onRemove, verticals }) {
  const aType = plat.accountType || 'new';
  const dKey = (plat.name === 'LSA' && aType === 'new') ? 'LSA_new' : plat.name;
  const rawList = PLAT_DELIVERABLES[dKey] || [];
  const hasGarageDoors = Array.isArray(verticals) && verticals.includes('Garage Doors');
  const garageExtras = hasGarageDoors ? (GARAGE_DOOR_DELIVERABLES[dKey] || []) : [];
  const dList = [...rawList, ...garageExtras].map(d => resolveLabel(d, aType));
  const showLP = LP_PLATFORMS.includes(plat.name);
  const lpDelivOn = plat.lpDelivEnabled !== false;

  function set(field, value) {
    onChange(index, { ...plat, [field]: value });
  }

  function setDeliverable(field, key, val) {
    onChange(index, {
      ...plat,
      [field]: { ...(plat[field] || {}), [key]: val },
    });
  }

  const allDelivDone = dList.length > 0 && dList.every(d => plat.deliverables?.[d]);

  return (
    <div style={{
      border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8,
      marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px', background: '#f5f5f3',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <Tag name={plat.name} />
        <span style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{plat.name}</span>
        <TogBtn active={plat.built} activeClass="built" onClick={() => set('built', !plat.built)}>Built</TogBtn>
        <TogBtn active={plat.posted} activeClass="posted" onClick={() => set('posted', !plat.posted)}>Posted</TogBtn>
        <TogBtn active={plat.launched} activeClass="launched" onClick={() => set('launched', !plat.launched)}>Launched</TogBtn>
        <TogBtn active={plat.live} activeClass="live" onClick={() => set('live', !plat.live)}>Live Ads</TogBtn>
        {plat.launched && (
          <input
            type="date"
            value={plat.launchDate || ''}
            onClick={e => e.stopPropagation()}
            onChange={e => set('launchDate', e.target.value)}
            title="Launch Date"
            style={{
              fontSize: 10, padding: '2px 5px', borderRadius: 5,
              border: '1px solid rgba(0,0,0,0.18)', background: '#EAF3DE',
              color: '#27500A', fontFamily: 'inherit', cursor: 'pointer',
            }}
          />
        )}
        {plat.name === 'LSA' && aType === 'existing' && (
          <TogBtn active={plat.auditDone} activeClass="audit" onClick={() => set('auditDone', !plat.auditDone)}>
            {plat.auditDone ? '✓ Audit Done' : 'Audit'}
          </TogBtn>
        )}
        <button onClick={() => onRemove(index)} style={{
          marginLeft: 'auto', background: 'none', border: 'none',
          cursor: 'pointer', color: '#aaa', fontSize: 14, padding: '2px 6px',
          borderRadius: 4,
        }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px' }}>
        {/* Meta row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <select
            value={aType}
            onChange={e => set('accountType', e.target.value)}
            style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f5f5f3' }}
          >
            <option value="new">New Account</option>
            <option value="existing">Existing Account</option>
          </select>
          <input
            type="text"
            placeholder={aType === 'existing' ? 'CID / ID Number' : 'CID / ID (after creation)'}
            value={plat.cid || ''}
            onChange={e => set('cid', e.target.value)}
            style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f5f5f3', width: 160 }}
          />
          {aType === 'existing' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!plat.accessGranted}
                onChange={e => set('accessGranted', e.target.checked)}
                style={{ accentColor: '#185FA5' }}
              />
              Access ✓
            </label>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#888' }}>$</span>
            <input
              type="number"
              placeholder="Budget"
              value={plat.budget || ''}
              onChange={e => set('budget', parseFloat(e.target.value) || '')}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f5f5f3', width: 90 }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>/mo</span>
            <input
              type="number"
              placeholder="Fee %"
              value={plat.feePercent || ''}
              onChange={e => set('feePercent', parseFloat(e.target.value) || '')}
              min="0" max="100"
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f0f7ff', width: 60 }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>%</span>
            {plat.budget && plat.feePercent ? (
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#0F6E56',
                background: '#EAF3DE', borderRadius: 5, padding: '2px 7px',
              }}>
                = ${Math.round((parseFloat(plat.budget) || 0) * (parseFloat(plat.feePercent) || 0) / 100).toLocaleString()}/mo
              </span>
            ) : null}
          </div>
        </div>

        {plat.name === 'Meta' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <input
              type="text"
              placeholder="Facebook Page URL"
              value={plat.facebookPageUrl || ''}
              onChange={e => set('facebookPageUrl', e.target.value)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f5f5f3', width: 220 }}
            />
            <select
              value={plat.metaPixelStatus || ''}
              onChange={e => set('metaPixelStatus', e.target.value)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.18)', background: '#f5f5f3' }}
            >
              <option value="">Meta Pixel: not set</option>
              <option value="installed">Meta Pixel: Installed</option>
              <option value="not_installed">Meta Pixel: Not Installed</option>
              <option value="unsure">Meta Pixel: Client Unsure</option>
            </select>
          </div>
        )}

        {/* Deliverables */}
        {dList.length > 0 && (
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: allDelivDone ? '#27500A' : '#854F0B',
              textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
            }}>
              {allDelivDone ? '✓ All Deliverables Received' : `Deliverables (${dList.filter(d => plat.deliverables?.[d]).length}/${dList.length})`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {dList.map(d => {
                const checked = !!(plat.deliverables?.[d]);
                return (
                  <label key={d} style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    fontSize: 12, padding: '4px 7px', borderRadius: 6, cursor: 'pointer',
                    color: checked ? '#999' : 'inherit',
                    textDecoration: checked ? 'line-through' : 'none',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => setDeliverable('deliverables', d, e.target.checked)}
                      style={{ accentColor: '#185FA5', width: 14, height: 14, flexShrink: 0, cursor: 'pointer' }}
                    />
                    {d}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* LP Deliverables */}
        {showLP && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#854F0B' }}>🗒 Landing Page Deliverables</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={lpDelivOn}
                  onChange={e => set('lpDelivEnabled', e.target.checked)}
                  style={{ accentColor: '#BA7517' }}
                />
                Applies
              </label>
            </div>
            {lpDelivOn && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {LP_DELIVERABLES.map(d => {
                  const checked = !!(plat.lpDeliverables?.[d]);
                  return (
                    <label key={d} style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      fontSize: 12, padding: '4px 7px', borderRadius: 6, cursor: 'pointer',
                      color: checked ? '#999' : 'inherit',
                      textDecoration: checked ? 'line-through' : 'none',
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => setDeliverable('lpDeliverables', d, e.target.checked)}
                        style={{ accentColor: '#BA7517', width: 14, height: 14, flexShrink: 0 }}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-channel notes */}
      <div style={{ padding: '0 12px 10px' }}>
        <textarea
          value={plat.notes || ''}
          onChange={e => set('notes', e.target.value)}
          placeholder={`Quick notes for ${plat.name} — what's missing, what's happening, hold-ups...`}
          style={{
            width: '100%', fontSize: 11, padding: '6px 9px', minHeight: 44,
            border: '1px solid rgba(0,0,0,0.15)', borderRadius: 6,
            background: '#fafaf8', color: '#333', fontFamily: 'inherit',
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );
}
